// Roles and capabilities for the OnLogic Team PWA.
//
// Authorization model (mirrors Section 3 of the plan):
//   - Every user has exactly one PRIMARY role (Employee | Contractor | Deactivated).
//   - On top of that, zero or more CAPABILITY roles stack to grant extra permissions.
//
// Both sets are stored on the Firebase Auth token as custom claims:
//   { role: PrimaryRole, capabilities: Capability[], costCenter?: string, site?: string }
//
// Security rules and Cloud Functions both read these claims to authorize work.

/** A user's single primary role. Mutually exclusive. */
export const PRIMARY_ROLES = ['employee', 'contractor', 'deactivated'] as const;
export type PrimaryRole = (typeof PRIMARY_ROLES)[number];

/** Capability roles. Stackable on top of the primary role. */
export const CAPABILITIES = [
  'facilitiesAgent',
  'facilitiesAdmin',
  'manager',
  'approver',
  // finance approves purchase requests above the manager-only threshold
  // (IMP-022 f). Distinct from the legacy `approver` capability; either may
  // claim a finance approval step, alongside the named finance pool in
  // config/purchaseRequests. Kept separate so Finance staff can be granted
  // PR finance-approval without the broader `approver` role.
  'finance',
  'executive',
  // contentAdmin curates reference content (e.g. /resources) without
  // needing systemAdmin. Distinct from systemAdmin so non-engineer
  // admins (HR, comms) can manage links without inheriting privileged
  // capabilities like role assignment or rules-write access.
  'contentAdmin',
  'fleetAdmin',
  'eventsAdmin',
  'surveyAdmin',
  'securityAdmin',
  // flightAdmin manages the corporate-aircraft module (docs/flights-module-plan.md):
  // reads flight requests incl. Class 3 manifests (audit-logged), confirms/declines
  // requests, triggers the single manifest egress, and manages the ID roster.
  'flightAdmin',
  'systemAdmin',
] as const;
export type Capability = (typeof CAPABILITIES)[number];

// Named constants for the role/capability string literals.
//
// Why (CLAUDE.md §6): call sites must never hard-code role strings. A bare
// 'systemAdmin' typo'd as 'systemAdminn' is a silent UX-gate bug with no
// compile error; importing CAP.systemAdmin turns each literal into a symbol
// the compiler checks. The `{ [K in …]: K }` annotation ties each map to its
// source-of-truth type: a member missing from CAP (or a value that doesn't
// equal its key) fails to compile, and adding a capability to CAPABILITIES
// forces a matching CAP entry. Values are the same strings, so
// CAP.systemAdmin === 'systemAdmin' (and is typed as the literal 'systemAdmin').

/** Capability name → itself, for de-literalized capability checks. */
export const CAP: { [K in Capability]: K } = {
  facilitiesAgent: 'facilitiesAgent',
  facilitiesAdmin: 'facilitiesAdmin',
  manager: 'manager',
  approver: 'approver',
  finance: 'finance',
  executive: 'executive',
  contentAdmin: 'contentAdmin',
  fleetAdmin: 'fleetAdmin',
  eventsAdmin: 'eventsAdmin',
  surveyAdmin: 'surveyAdmin',
  securityAdmin: 'securityAdmin',
  flightAdmin: 'flightAdmin',
  systemAdmin: 'systemAdmin',
};

/** Primary-role name → itself, for de-literalized primary-role checks. */
export const ROLE: { [K in PrimaryRole]: K } = {
  employee: 'employee',
  contractor: 'contractor',
  deactivated: 'deactivated',
};

/** Shape of the custom claims block on a Firebase Auth token. */
export interface OnLogicClaims {
  role: PrimaryRole;
  capabilities: Capability[];
  /** HRIS cost center, used for routing PR approvals. */
  costCenter?: string;
  /** Primary site code (e.g. "USQSB", "TWNTC"). Used for default audience filters. */
  site?: string;
  /** Manager UID, used for direct-report access (annual reviews, PRs within manager limit). */
  managerUid?: string;
}

/** Type guard for primary roles. */
export function isPrimaryRole(value: unknown): value is PrimaryRole {
  return typeof value === 'string' && (PRIMARY_ROLES as readonly string[]).includes(value);
}

/** Type guard for capabilities. */
export function isCapability(value: unknown): value is Capability {
  return typeof value === 'string' && (CAPABILITIES as readonly string[]).includes(value);
}

/** Whether a claims block has a given capability. Defensive against missing fields. */
export function hasCapability(claims: Partial<OnLogicClaims> | undefined, cap: Capability): boolean {
  return Array.isArray(claims?.capabilities) && claims!.capabilities!.includes(cap);
}

/** Whether a claims block has at least one of the given capabilities. */
export function hasAnyCapability(
  claims: Partial<OnLogicClaims> | undefined,
  caps: readonly Capability[],
): boolean {
  return caps.some((cap) => hasCapability(claims, cap));
}

/**
 * The capability set that makes a user "facilities staff". One definition for
 * every gate — the web UI (queue tabs, detail actions), the callables
 * (updateTicketStatus, assignTicket, listFacilitiesAgents, queue metrics), and
 * firestore.rules all admit the same three capabilities. The server previously
 * gated on `facilitiesAgent` alone, so a facilitiesAdmin/systemAdmin without
 * the agent capability saw agent surfaces the server then rejected.
 */
export const FACILITIES_STAFF_CAPS: readonly Capability[] = [
  CAP.facilitiesAgent,
  CAP.facilitiesAdmin,
  CAP.systemAdmin,
];

/** Whether a claims block is facilities staff (agent, facilities admin, or system admin). */
export function isFacilitiesStaff(claims: Partial<OnLogicClaims> | undefined): boolean {
  return hasAnyCapability(claims, FACILITIES_STAFF_CAPS);
}

/** Whether a user is active (non-deactivated). */
export function isActive(claims: Partial<OnLogicClaims> | undefined): boolean {
  return claims?.role === 'employee' || claims?.role === 'contractor';
}
